import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CallStatus = 'scheduled' | 'active' | 'completed' | 'training' | 'cancelled' | 'no_show';
export type ViewMode = 'personal' | 'team';

export interface CalendarCall {
  id: string;
  call_id: string;
  room_name: string | null;
  officer_name: string | null;
  case_number: string | null;
  start_time: string | null;
  end_time: string | null;
  duration: number | null;
  caller_name: string | null;
  call_summary: string | null;
  incident_type: string | null;
  owner_id: string | null;
  scheduled_time: string | null;
  assigned_officer_id: string | null;
  is_simulated: boolean;
  call_type: string | null;
  // Joined data
  has_report: boolean;
  has_recording: boolean;
  has_transcript: boolean;
  // Computed
  status: CallStatus;
  // Phase 2: Scheduling fields (present when sourced from scheduled_calls)
  source?: 'calls' | 'scheduled_calls';
  scheduled_call_id?: string | null;
  estimated_duration?: number | null;
  caller_phone?: string | null;
  caller_email?: string | null;
  notes?: string | null;
  confirmation_status?: 'scheduled' | 'confirmed' | 'declined' | 'cancelled' | 'no_show' | null;
  is_training?: boolean;
  training_persona_id?: string | null;
  training_scenario?: string | null;
  training_censoring_level?: number | null;
}

export interface CalendarFilters {
  incidentType: string | null;
  status: CallStatus | null;
  searchQuery: string;
}

export interface CalendarState {
  currentWeekStart: string; // ISO date string (YYYY-MM-DD)
  calls: CalendarCall[];
  selectedCallId: string | null;
  viewMode: ViewMode;
  filters: CalendarFilters;
  loading: boolean;
  error: string | null;
  scheduleDialogOpen: boolean;
  scheduleDialogPrefillTime: string | null;
}

/**
 * Formats a Date as YYYY-MM-DD in the LOCAL timezone.
 * IMPORTANT: Do NOT use toISOString().split('T')[0] — that converts to UTC
 * and can shift the date by a day depending on the user's timezone.
 */
function toLocalDateStr(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Returns the Sunday of the week containing the given date as YYYY-MM-DD string.
 * Week runs Sunday (0) through Saturday (6).
 * JS getDay(): Sun=0, Mon=1, ..., Fri=5, Sat=6
 */
function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun .. 6=Sat
  d.setDate(d.getDate() - day);
  return toLocalDateStr(d);
}

/**
 * Adds the specified number of days to a YYYY-MM-DD date string and returns a new YYYY-MM-DD string.
 */
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return toLocalDateStr(d);
}

const initialState: CalendarState = {
  currentWeekStart: getWeekStart(new Date()),
  calls: [],
  selectedCallId: null,
  viewMode: 'personal',
  filters: {
    incidentType: null,
    status: null,
    searchQuery: '',
  },
  loading: true,
  error: null,
  scheduleDialogOpen: false,
  scheduleDialogPrefillTime: null,
};

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    setWeekStart: (state, action: PayloadAction<string>) => {
      state.currentWeekStart = action.payload;
    },
    setCalls: (state, action: PayloadAction<CalendarCall[]>) => {
      state.calls = action.payload;
    },
    updateCall: (state, action: PayloadAction<Partial<CalendarCall> & { id: string }>) => {
      const index = state.calls.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        state.calls[index] = { ...state.calls[index], ...action.payload };
      }
    },
    addCall: (state, action: PayloadAction<CalendarCall>) => {
      state.calls.push(action.payload);
    },
    removeCall: (state, action: PayloadAction<string>) => {
      state.calls = state.calls.filter((c) => c.id !== action.payload);
    },
    selectCall: (state, action: PayloadAction<string | null>) => {
      state.selectedCallId = action.payload;
    },
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<CalendarFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    navigateWeek: (state, action: PayloadAction<'prev' | 'next' | 'today'>) => {
      switch (action.payload) {
        case 'prev':
          state.currentWeekStart = addDays(state.currentWeekStart, -7);
          break;
        case 'next':
          state.currentWeekStart = addDays(state.currentWeekStart, 7);
          break;
        case 'today':
          state.currentWeekStart = getWeekStart(new Date());
          break;
      }
    },
    openScheduleDialog: (state, action: PayloadAction<string | null>) => {
      state.scheduleDialogOpen = true;
      state.scheduleDialogPrefillTime = action.payload;
    },
    closeScheduleDialog: (state) => {
      state.scheduleDialogOpen = false;
      state.scheduleDialogPrefillTime = null;
    },
  },
});

export const {
  setWeekStart,
  setCalls,
  updateCall,
  addCall,
  removeCall,
  selectCall,
  setViewMode,
  setFilters,
  setLoading,
  setError,
  navigateWeek,
  openScheduleDialog,
  closeScheduleDialog,
} = calendarSlice.actions;
export default calendarSlice.reducer;
